import { isFuture } from 'date-fns';
import {
    cloneDeep,
    compact,
    endsWith,
    filter,
    find,
    forEach,
    get,
    groupBy,
    isEqual,
    isString,
    map,
    mapValues,
    random,
    sortBy,
    uniq,
} from 'lodash';
import { Descendant, Node } from 'slate';
import { v4 } from 'uuid';
import {
    CliArgumentConfigurationView,
    CliArgumentEnum,
    CliArgumentEnumType,
    CliArgumentTypeEnum,
    CliArgumentUsageTypeEnum,
    CliAudit,
    CliAuditStatusEnumType,
    CliAutocompleteOption,
    CliExecutionView,
    ConsolidatedLocationView,
} from '@mark43/cad-api';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { WorkspacePanel } from '../panels/workspace-panel';
import { CommandEntityMap } from './command-line-editor-store';
import {
    matchOperatorsRe,
    segmentTypes,
    TEXT_COMMENTS_REGEX,
    WHITESPACE_ALPHANUMERIC_REGEX,
    WHITESPACE_REGEX,
} from './constants';
import { getLocationSummarySubpremises } from '../location-select/helpers/location-display';
import { LocationStoreView } from '../location-select/types/locations';
import { GenericObject } from '../common/types';
import {
    CommandLineHistoryView,
    CommandSegment,
    CommentSegment,
    DataExchangeInquiryParametersSegment,
    EventSegment,
    FormFieldSegment,
    HighPrioritySegment,
    LicensePlateSegment,
    LocationSegment,
    ParserCommand,
    ParserConfig,
    Segment,
    UnitSegment,
} from './types';
import { commandStatusEnum, CommandStatusEnumType } from './types/command-status-enum';
import {
    COMPACT_CLI_HEIGHT,
    STANDARD_CLI_HEIGHT,
    THRESHOLD_CLI_HEIGHT,
} from './use-open-command-line-panel';

export const DEFAULT_COMMAND_SUCCESS_MESSAGE = 'Command Successful';

export const DEFAULT_COMMAND_ERROR_MESSAGE = 'There was an error executing this command';

export const isFormField = (segment: Segment): boolean => {
    const cliObject = CliArgumentEnum[segment.segmentType as CliArgumentEnumType];

    return cliObject?.argumentType === CliArgumentTypeEnum.FORM_IDENTIFIER.name;
};

export const removeOverlappingSegments = (segments: Segment[]): Segment[] => {
    const filteredSegments: Segment[] = [];
    const sortedSegments = segments.sort((segment1, segment2) => {
        if (segment1.start !== segment2.start) {
            return segment1.start < segment2.start ? -1 : 1;
        }
        // prioritize shorter segments to prevent a situation where a closing/ending tag extends into another segment
        // For example: VS @123 CULVER STREET@ where "V" is also the tag for vehicles, and "VS" is a command
        if (segment1.length !== segment2.length) {
            return segment1.length < segment2.length ? -1 : 1;
        }
        // prioritize commands over other segments. By the time this code runs we've filtered down the command list
        // to at most 1, so we should use that command
        if (
            segment1.segmentType === segmentTypes.COMMAND ||
            segment2.segmentType === segmentTypes.COMMAND
        ) {
            return segment1.segmentType === segmentTypes.COMMAND ? -1 : 1;
        }
        return 0;
    });
    let currentSegment: Segment | null = null;

    forEach(sortedSegments, (segment) => {
        if (!currentSegment || segment.start >= currentSegment.start + currentSegment.length) {
            currentSegment = segment;
            filteredSegments.push(segment);
        }
    });
    return filteredSegments;
};

export function isDateInFuture(date?: string | number | Date): boolean {
    if (!date) {
        return false;
    }

    if (isString(date)) {
        return isFuture(new Date(date));
    }
    return isFuture(date);
}
export const reconcile = (segments: Segment[]): Segment[] => {
    let results = cloneDeep(segments);
    const groupedBySegmentType = mapValues(groupBy(segments, 'segmentType'), (segments) =>
        sortBy(segments, 'start')
    );

    const possibleCommands = groupedBySegmentType[segmentTypes.COMMAND];
    if (possibleCommands && possibleCommands.length > 1) {
        results = filter(results, (segment) => {
            if (
                segment.segmentType === segmentTypes.COMMAND &&
                !isEqual(segment, possibleCommands[0])
            ) {
                return false;
            }

            return true;
        });
    }

    return removeOverlappingSegments(results);
};

export const findSegmentForOffset = (segments: Segment[], offset: number): Segment | null => {
    const filtered = filter(segments, ({ start, length }) => {
        return offset >= start && offset <= start + length;
    });

    if (filtered.length) {
        return filtered[0];
    }

    return null;
};

export const getActiveCommandSegment = (segments: Segment[]): Segment | null | undefined => {
    return find(segments, { segmentType: segmentTypes.COMMAND });
};

export const getActiveCommand = (
    segments: Segment[],
    commands: ParserCommand[]
): ParserCommand | null | undefined => {
    const activeCommandSegment = getActiveCommandSegment(segments);
    return find(commands, {
        alias: get(activeCommandSegment, 'segmentData.alias'),
    });
};

// export const getFirstUnitSegmentFromHydratedSegmentMap = <T>(
//     hydratedSegmentMap: Record<string, T[]>
// ): UnitSegment | null | undefined => head(hydratedSegmentMap[segmentTypes.UNIT]);

export const findCommandsInString = (
    str = '',
    commands: Record<string, CliArgumentConfigurationView>
): CommandSegment[] => {
    const tokens = getTokens(str);

    if (!tokens.length || !Object.keys(commands).length) {
        return [];
    }

    let results: CommandSegment[] = [];
    const [first, second] = tokens;
    results = compact(
        map([first, second], (token) => {
            if (!token) {
                return null;
            }

            if (token.value && commands[token.value.toUpperCase()]) {
                return {
                    segmentType: segmentTypes.COMMAND,
                    segmentData: commands[token.value.toUpperCase()],
                    start: token.index,
                    length: token.length,
                };
            }

            return undefined;
        })
    );

    return results;
};

export const findTextCommentsInString = (str = ''): CommentSegment[] => {
    const comments = tokenize(TEXT_COMMENTS_REGEX)(str);

    return compact(
        map(comments, (token) => {
            return {
                segmentType: segmentTypes.COMMENT,
                segmentData: token.value,
                start: token.index,
                length: token.length,
            };
        })
    );
};

export const findLocationsInString = (str = '', locationToken = ''): LocationSegment[] => {
    if (!locationToken) {
        return [];
    }

    const locationRegex = new RegExp(
        `(${escapeRegex(locationToken)}[^${escapeRegex(locationToken)}]*${escapeRegex(
            locationToken
        )})`,
        'g'
    );

    const locations = tokenize(locationRegex)(str);

    return compact(
        map(locations, (token) => {
            return {
                segmentType: segmentTypes.LOCATION,
                segmentData: token.value,
                start: token.index,
                length: token.length,
            };
        })
    );
};

export const findDexInquiryParamsInString = (
    str: string,
    dexInquiryParamsToken: string
): DataExchangeInquiryParametersSegment[] => {
    if (!dexInquiryParamsToken) {
        return [];
    }

    const dexInquiryParamsRegex = new RegExp(
        `${escapeRegex(dexInquiryParamsToken)}([A-Z0-9-${escapeRegex(
            dexInquiryParamsToken
        )}]*)${escapeRegex(dexInquiryParamsToken)}( |$)`,
        'g'
    );

    const dexInquiryParamses = tokenize(dexInquiryParamsRegex)(str?.toUpperCase());

    const ret = compact(
        map(dexInquiryParamses, (token) => {
            return {
                segmentType: segmentTypes.DATA_EXCHANGE_INQUIRY_PARAMETERS,
                segmentData: token.value,
                start: token.index,
                length: token.length,
            };
        })
    );

    return ret;
};

export const findHighPriorityInString = (
    str: string,
    highPriorityToken = ''
): HighPrioritySegment[] => {
    if (!highPriorityToken) {
        return [];
    }
    const highPriorityTokenRegex = new RegExp(`(${escapeRegex(highPriorityToken)})`, 'g');

    const highPriorityTokens = tokenize(highPriorityTokenRegex)(str);

    return compact(
        map(highPriorityTokens, (token) => {
            return {
                segmentType: segmentTypes.IS_HIGH_PRIORITY,
                segmentData: token.value,
                start: token.index,
                length: token.length,
            };
        })
    );
};

export const findFormFieldsInString = (
    str: string,
    formFieldSeparator = ':',
    formFieldDelimiter = ';',
    cliArgumentForArgumentDefinition: Record<string, CliArgumentEnumType> = {}
): FormFieldSegment[] => {
    const formFields = tokenize(createFormRegex(formFieldSeparator, formFieldDelimiter))(str);

    const ret: FormFieldSegment[] = compact(
        map(formFields, (token) => {
            const fieldSegment = {
                segmentType: 'temp',
                segmentData: token.value,
                start: token.index,
                length: token.length,
            };

            const { key } = getKeyValueFromFormFieldSegment(fieldSegment, {
                formFieldSeparator,
                formFieldDelimiter,
            });

            const segmentType =
                segmentTypes[
                    cliArgumentForArgumentDefinition[key.toUpperCase()] as keyof typeof segmentTypes
                ];

            if (!segmentType) {
                return null;
            }

            return {
                segmentType: segmentType as FormFieldSegment['segmentType'],
                segmentData: token.value,
                start: token.index,
                length: token.length,
            };
        })
    );

    return ret;
};

export const findEventsInString = (str = '', eventToken = ''): EventSegment[] => {
    if (!eventToken) {
        return [];
    }

    const eventRegex = new RegExp(`(${escapeRegex(eventToken)}[${'\\'}S-]+)`, 'g');
    const events = tokenize(eventRegex)(str);

    return compact(
        map(events, (token) => {
            return {
                segmentType: segmentTypes.EVENT,
                segmentData: token.value,
                start: token.index,
                length: token.length,
            };
        })
    );
};

export const findUnitsInString = (str = ''): UnitSegment[] => {
    const units = tokenize(WHITESPACE_ALPHANUMERIC_REGEX)(str);

    return compact(
        map(units, (token) => {
            return {
                segmentType: segmentTypes.UNIT,
                segmentData: token.value,
                start: token.index,
                length: token.length,
            };
        })
    );
};

export const findLicensePlatesInString = (
    str: string,
    licensePlateToken: string
): LicensePlateSegment[] => {
    if (!licensePlateToken) {
        return [];
    }
    const licensePlateRegex = new RegExp(
        `(${escapeRegex(licensePlateToken)}[^${escapeRegex(licensePlateToken)}]*${escapeRegex(
            licensePlateToken
        )})`,
        'g'
    );

    const licensePlates = tokenize(licensePlateRegex)(str);

    return compact(
        map(licensePlates, (token) => {
            return {
                segmentType: segmentTypes.LICENSE_PLATE,
                segmentData: token.value,
                start: token.index,
                length: token.length,
            };
        })
    );
};

export const createFormRegex = (formFieldSeparator = ':', formFieldDelimiter = ';'): RegExp =>
    new RegExp(
        `\\s([\\w-]+${formFieldSeparator}\\s*(?${formFieldSeparator}(?${formFieldSeparator}[^${formFieldDelimiter}${formFieldSeparator}]*${formFieldDelimiter})|[\\w-]*))`,
        'g'
    );

export const tokenize = (regexPattern: string | RegExp) => (str: string) => {
    const regex = new RegExp(regexPattern);
    let match;
    const tokens = [];
    while ((match = regex.exec(str))) {
        tokens.push({
            value: match[1],
            index: match.index + match[0].indexOf(match[1]),
            length: match[1].length,
        });
    }
    return tokens;
};

export const getTokens = (
    str = ''
): {
    value: string;
    index: number;
    length: number;
}[] => (str ? tokenize(WHITESPACE_REGEX)(str) : []);

export const escapeRegex = (str = ''): string => {
    return str.replace(matchOperatorsRe, '\\$&');
};

export const getKeyValueFromFormFieldSegment = (
    segment: any,
    { formFieldSeparator = ':', formFieldDelimiter = ';' } = {}
): { key: string; value: string } => {
    // eslint-disable-next-line prefer-const
    let [key, value] = (get(segment, 'segmentData', '') as string).split(formFieldSeparator);

    if (endsWith(value, formFieldDelimiter)) {
        value = value.slice(0, -1);
    }

    return { key, value };
};

export const findAllSegmentsInString = ({
    cliArgumentForArgumentDefinition,
    commands = {},
    licensePlateToken = '',
    highPriorityToken = '',
    locationToken = '',
    dexInquiryParamsToken = '',
    eventToken = '',
    formFieldSeparator = ':',
    formFieldDelimiter = ';',
}: ParserConfig) => (str = '') => {
    return reconcile(
        sortBy(
            [
                ...findTextCommentsInString(str),
                ...findDexInquiryParamsInString(str, dexInquiryParamsToken),
                ...findLocationsInString(str, locationToken),
                ...findLicensePlatesInString(str, licensePlateToken),
                ...findHighPriorityInString(str, highPriorityToken),
                ...findFormFieldsInString(
                    str,
                    formFieldSeparator,
                    formFieldDelimiter,
                    cliArgumentForArgumentDefinition
                ),
                ...findEventsInString(str, eventToken),
                ...findCommandsInString(str, commands),
                ...findUnitsInString(str),
            ],
            'start'
        )
    );
};

export const autoCloseTokenInString = (
    {
        token,
        string,
        lastKeyPressed,
        offset,
        withinString = false,
        closingToken = '',
    }: {
        token: string;
        string: string;
        lastKeyPressed: string;
        offset: number;
        withinString?: boolean;
        closingToken?: string;
    } = { token: '', string: '', lastKeyPressed: '', offset: 0 }
): { string: string | null; offset: number | null } => {
    if (lastKeyPressed === token) {
        const precedingCharacter = string[offset - 1];
        // const afterCharacter = string[offset + 1];
        if (
            offset === 0 ||
            precedingCharacter === ' ' ||
            (precedingCharacter !== ' ' && withinString)
        ) {
            const newString = `${string.slice(0, offset)}${lastKeyPressed}${
                closingToken || lastKeyPressed
            }${string.slice(offset)}`;
            return {
                string: newString,
                offset: offset + 1,
            };
        }
    }

    return { string: null, offset: null };
};

export const cliAuditStatusToCommandStatus = (
    cliAuditStatus: CliAuditStatusEnumType
): CommandStatusEnumType => {
    switch (cliAuditStatus) {
        case 'SUCCESS':
            return commandStatusEnum.SUCCESS;
        case 'RUNTIME_ERROR':
        case 'VALIDATION_ERROR':
            return commandStatusEnum.ERROR;
        case 'RECEIVED':
            return commandStatusEnum.RUNNING;
        default:
            return commandStatusEnum.ERROR;
    }
};

export function convertCliAuditToCommandLineHistoryView(
    cliAudit: CliAudit
): CommandLineHistoryView {
    const {
        id,
        cliAuditStatus,
        commandText,
        cliArgumentConfigurationId,
        updatedDateUtc,
        createdDateUtc,
        serializedCliExecutionResponseTokens,
        errorMessage,
    } = cliAudit;

    let successText;
    if (serializedCliExecutionResponseTokens) {
        try {
            successText = JSON.parse(serializedCliExecutionResponseTokens);
        } catch (e) {
            successText = '';
        }
    }

    // At this point success text will either be an array or ''
    successText =
        successText && successText.length
            ? successText
            : [{ text: DEFAULT_COMMAND_SUCCESS_MESSAGE }];

    const status = cliAuditStatusToCommandStatus(cliAuditStatus);

    let executionView: CliExecutionView | undefined;
    let commandEntityMap: CommandEntityMap | undefined;
    try {
        executionView = JSON.parse(cliAudit.serializedCliExecutionView) as CliExecutionView;

        if (executionView && executionView.serializedCommandEntityMap) {
            try {
                commandEntityMap = JSON.parse(
                    executionView.serializedCommandEntityMap
                ) as CommandEntityMap;
            } catch (e) {
                // Error parsing command entity map - silently handled
            }
        }
    } catch (e) {
        // Error parsing execution view - silently handled
    }

    return {
        id,
        executionView,
        commandEntityMap,
        startTime: createdDateUtc,
        endTime: updatedDateUtc,
        cliArgumentConfigurationId,
        hidden: false,
        commandText,
        status,
        partialSuccessMessage: errorMessage,
        errors: status === commandStatusEnum.ERROR ? DEFAULT_COMMAND_ERROR_MESSAGE : undefined,
        successText,
    };
}

function exampleTextForSegmentType(
    segmentType: CliArgumentEnumType,
    configView: CliArgumentConfigurationView,
    config: CommandLineConfigView
): string {
    switch (segmentType) {
        case CliArgumentEnum.UNIT.name:
            return `${random(100, 250)}`;
        case CliArgumentEnum.EVENT.name:
            return `#${random(100000, 800000)}`;
        case CliArgumentEnum.COMMENT.name:
            return ',some comments';
        case CliArgumentEnum.DISPATCH_AREA.name:
            return `${config.tokens.dispatchAreaFieldDefinition}:DArea`;
        case CliArgumentEnum.AGENCY_EVENT_RADIO_CHANNELS.name:
            return `${config.tokens.radioChannelFieldDefinition}:Radio Channel;`;
        case CliArgumentEnum.MEMBER.name:
            return `${config.tokens.memberFieldDefinition}:Name;`;
        case CliArgumentEnum.PRIMARY_SUBDIVISION.name:
            return `${config.tokens.primarySubdivisionFieldDefinition}:Primary Subdivision;`;
        case CliArgumentEnum.MESSAGE_GROUP.name:
            return `${config.tokens.messageGroupDefinition}:Group Name;`;
        case CliArgumentEnum.CALL_TAKER_STATION.name:
            return `${config.tokens.callTakerStationDefinition}:Call Station Name;`;
        case CliArgumentEnum.AGENCY.name:
            return `${config.tokens.agencyFieldDefinition}:XYZ`;
        case CliArgumentEnum.VEHICLE.name:
            return `${config.tokens.vehicleToken}:1234`;
        case CliArgumentEnum.LOCATION.name:
            return `${config.tokens.locationToken}1234 Main Street${config.tokens.locationToken}`;
        case CliArgumentEnum.DATA_EXCHANGE_INQUIRY_PARAMETERS.name: {
            const delimiter = config.tokens.dexInquiryParamsToken || '.';
            const innerText = map(configView.dexCommandParams, 'name').join(delimiter);
            return `${delimiter}${innerText}${delimiter}`;
        }
        default:
            return '';
    }
}

function createExampleText(
    configView: CliArgumentConfigurationView,
    config: CommandLineConfigView
): string {
    const exampleText = [
        configView.argumentDefinition,
        ...map(configView.cliArgumentConfigurationLinks, (link) => {
            return exampleTextForSegmentType(link.cliArgument, configView, config);
        }),
    ].join(' ');

    return exampleText;
}

export const helpersEnum = {
    [CliArgumentEnum.LOCATION.name]: CliArgumentEnum.LOCATION.name,
    [CliArgumentEnum.EVENT.name]: CliArgumentEnum.EVENT.name,
    [CliArgumentEnum.HELP.name]: CliArgumentEnum.HELP.name,
    [CliArgumentEnum.DISPATCH_AREA.name]: CliArgumentEnum.DISPATCH_AREA.name,
    [CliArgumentEnum.DISPATCH_AREA_GROUP.name]: CliArgumentEnum.DISPATCH_AREA_GROUP.name,
    [CliArgumentEnum.MEMBER.name]: CliArgumentEnum.MEMBER.name,
    [CliArgumentEnum.AGENCY.name]: CliArgumentEnum.AGENCY.name,
    [CliArgumentEnum.CALL_FOR_SERVICE.name]: CliArgumentEnum.CALL_FOR_SERVICE.name,
    [CliArgumentEnum.VEHICLE.name]: CliArgumentEnum.VEHICLE.name,
    [CliArgumentEnum.DISPOSITION.name]: CliArgumentEnum.DISPOSITION.name,
    [CliArgumentEnum.ROTATION_SERVICE_TYPE.name]: CliArgumentEnum.ROTATION_SERVICE_TYPE.name,
    [CliArgumentEnum.MESSAGE_GROUP.name]: CliArgumentEnum.MESSAGE_GROUP.name,
    [CliArgumentEnum.UNIT_TAG.name]: CliArgumentEnum.UNIT_TAG.name,
    [CliArgumentEnum.CALL_TAKER_STATION.name]: CliArgumentEnum.CALL_TAKER_STATION.name,
    [CliArgumentEnum.STATION.name]: CliArgumentEnum.STATION.name,
    [CliArgumentEnum.PRIMARY_SUBDIVISION.name]: CliArgumentEnum.PRIMARY_SUBDIVISION.name,
    [CliArgumentEnum.AGENCY_EVENT_RADIO_CHANNELS.name]:
        CliArgumentEnum.AGENCY_EVENT_RADIO_CHANNELS.name,
    // [CliArgumentEnum.TIME.name]: CliArgumentEnum.TIME.name,
    [CliArgumentEnum.CAD_REQUEST_PROFILE.name]: CliArgumentEnum.CAD_REQUEST_PROFILE.name,
    [CliArgumentEnum.HOSPITAL.name]: CliArgumentEnum.HOSPITAL.name,
    [CliArgumentEnum.ORI_ALIAS.name]: CliArgumentEnum.ORI_ALIAS.name,
    [CliArgumentEnum.MESSAGE_RECIPIENT.name]: CliArgumentEnum.MESSAGE_RECIPIENT.name,
} as const;

export type HelperName = keyof typeof helpersEnum;

export interface HelpObject {
    exampleText: string;
    searchText: string;
    cliArgumentConfigurationView: CliArgumentConfigurationView;
}

export interface ConfigTokens {
    dexInquiryParamsToken?: string;
    eventToken?: string;
    locationToken?: string;
    vehicleToken?: string;
    licensePlateToken?: string;
    highPriorityToken?: string;
    formFieldSeparator?: string;
    formFieldDelimiter?: string;
    dispatchAreaFieldDefinition?: string;
    radioChannelFieldDefinition?: string;
    memberFieldDefinition?: string;
    primarySubdivisionFieldDefinition?: string;
    messageGroupDefinition?: string;
    unitTagDefinition?: string;
    callTakerStationDefinition?: string;
    agencyFieldDefinition?: string;
    callForServiceDefinition?: string;
    dispositionDefinition?: string;
    dispatchAreaGroupDefinition?: string;
    stationDefinition?: string;
    timeDefinition?: string;
    rotationServiceTypeDefinition?: string;
    requestProfileDefinition?: string;
}

export type ConfigTokenName = keyof ConfigTokens;

export function useConfigTokenDisplayName(): (tokenName: ConfigTokenName) => string {
    const { primaryPoliceSubdivisionName } = useBootstrapState();
    return function getConfigTokenDisplayName(tokenName: ConfigTokenName): string {
        switch (tokenName) {
            case 'agencyFieldDefinition':
                return 'Agency';
            case 'callTakerStationDefinition':
                return 'Station';
            case 'dexInquiryParamsToken':
                return 'DEX Inquiry Params';
            case 'dispatchAreaFieldDefinition':
                return 'Dispatch Area';
            case 'eventToken':
                return 'Event';
            case 'formFieldDelimiter':
                return 'Form Field Delimiter';
            case 'formFieldSeparator':
                return 'Form Field Separator';
            case 'highPriorityToken':
                return 'High Priority';
            case 'licensePlateToken':
                return 'License Plate';
            case 'locationToken':
                return 'Location';
            case 'memberFieldDefinition':
                return 'Member';
            case 'messageGroupDefinition':
                return 'Message Group';
            case 'primarySubdivisionFieldDefinition':
                return primaryPoliceSubdivisionName;
            case 'radioChannelFieldDefinition':
                return 'Radio Channel';
            case 'unitTagDefinition':
                return 'Call Sign';
            case 'vehicleToken':
                return 'Vehicle';
            case 'callForServiceDefinition':
                return 'CFS';

            case 'dispositionDefinition':
                return 'Disposition';
            case 'dispatchAreaGroupDefinition':
                return 'Dispatch Area Group';
            case 'stationDefinition':
                return 'Station';
            case 'timeDefinition':
                return 'Time';
            case 'rotationServiceTypeDefinition':
                return 'Rotation Service Type';
            case 'requestProfileDefinition':
                return 'Request Profile';

            default:
                return tokenName;
        }
    };
}

export interface CommandLineConfigView {
    tokens: ConfigTokens;
    cliArgumentForArgumentDefinition: Record<string, CliArgumentEnumType>;
    commands: Record<string, CliArgumentConfigurationView>;
    helpText: HelpObject[];
    views: CliArgumentConfigurationView[];
}

const commandLineTokenNameMap: Partial<
    Record<CliArgumentEnumType, keyof CommandLineConfigView['tokens']>
> = {
    [CliArgumentEnum.LOCATION.name]: 'locationToken',
    [CliArgumentEnum.EVENT.name]: 'eventToken',
    [CliArgumentEnum.LICENSE_PLATE.name]: 'licensePlateToken',
    [CliArgumentEnum.FORM_SEPARATOR.name]: 'formFieldSeparator',
    [CliArgumentEnum.FORM_DELIMITER.name]: 'formFieldDelimiter',
    [CliArgumentEnum.DATA_EXCHANGE_INQUIRY_PARAMETERS.name]: 'dexInquiryParamsToken',
    [CliArgumentEnum.IS_HIGH_PRIORITY.name]: 'highPriorityToken',
    [CliArgumentEnum.DISPATCH_AREA.name]: 'dispatchAreaFieldDefinition',
    [CliArgumentEnum.MEMBER.name]: 'memberFieldDefinition',
    [CliArgumentEnum.VEHICLE.name]: 'vehicleToken',
    [CliArgumentEnum.CALL_FOR_SERVICE.name]: 'callForServiceDefinition',
    [CliArgumentEnum.DISPOSITION.name]: 'dispositionDefinition',
    [CliArgumentEnum.AGENCY.name]: 'agencyFieldDefinition',
    [CliArgumentEnum.DISPATCH_AREA_GROUP.name]: 'dispatchAreaGroupDefinition',
    [CliArgumentEnum.STATION.name]: 'stationDefinition',
    [CliArgumentEnum.MESSAGE_GROUP.name]: 'messageGroupDefinition',
    [CliArgumentEnum.CALL_TAKER_STATION.name]: 'callTakerStationDefinition',
    [CliArgumentEnum.UNIT_TAG.name]: 'unitTagDefinition',
    [CliArgumentEnum.TIME.name]: 'timeDefinition',
    [CliArgumentEnum.ROTATION_SERVICE_TYPE.name]: 'rotationServiceTypeDefinition',
    [CliArgumentEnum.PRIMARY_SUBDIVISION.name]: 'primarySubdivisionFieldDefinition',
    [CliArgumentEnum.AGENCY_EVENT_RADIO_CHANNELS.name]: 'radioChannelFieldDefinition',
    [CliArgumentEnum.CAD_REQUEST_PROFILE.name]: 'requestProfileDefinition',
};

export function convertCommandLineArgumentConfigurationViewsToCommandLineConfigView(
    views: CliArgumentConfigurationView[] = []
): CommandLineConfigView {
    const config: CommandLineConfigView = {
        views,
        cliArgumentForArgumentDefinition: {},
        tokens: {},
        commands: {},
        helpText: [],
    };

    views.forEach((view) => {
        const cliArgumentObject = CliArgumentEnum[view.cliArgument as CliArgumentEnumType];

        if (!cliArgumentObject) {
            return;
        }
        const normalizedArgumentDefinition = view.argumentDefinition.toUpperCase();
        config.cliArgumentForArgumentDefinition[normalizedArgumentDefinition] = view.cliArgument;
        if (cliArgumentObject.argumentType === CliArgumentTypeEnum.COMMAND.name) {
            config.commands[normalizedArgumentDefinition] = view;
        } else if (
            cliArgumentObject.argumentType === CliArgumentTypeEnum.FORM_IDENTIFIER.name ||
            cliArgumentObject.argumentType === CliArgumentTypeEnum.TOKEN.name
        ) {
            const configViewName = commandLineTokenNameMap[view.cliArgument];

            if (configViewName) {
                config.tokens[configViewName] = view.argumentDefinition;
            }
        }
    });

    // Generate Help Text after Config is complete
    views.forEach((view) => {
        const cliArgumentObject = CliArgumentEnum[view.cliArgument as CliArgumentEnumType];
        if (!cliArgumentObject) {
            return;
        }
        if (cliArgumentObject.argumentType === CliArgumentTypeEnum.COMMAND.name) {
            config.helpText.push({
                exampleText: createExampleText(view, config),
                cliArgumentConfigurationView: view,
                searchText: [view.argumentDefinition, view.title, view.description]
                    .join(' ')
                    .toLowerCase(),
            });
        }
    });

    return config;
}

export const getRepeatableCliArguments = (
    cliArgumentConfig: CliArgumentConfigurationView
): CliArgumentEnumType[] => {
    const ret = uniq(
        map(
            filter(cliArgumentConfig.cliArgumentConfigurationLinks, {
                cliArgumentUsageType: CliArgumentUsageTypeEnum.REPEATING.name,
            }),
            'cliArgument'
        )
    );

    return ret;
};

export function toggleCommandLineHeight(workspacePanel: WorkspacePanel) {
    let height = workspacePanel.height;
    if (height < THRESHOLD_CLI_HEIGHT) {
        height = STANDARD_CLI_HEIGHT;
    } else {
        height = COMPACT_CLI_HEIGHT;
    }

    workspacePanel.setDimensions(workspacePanel.width, height);
    workspacePanel.synchronizeLayout();
}

export function getPotentialUnitCallSigns(
    commandText: string,
    currentSegments: Segment[]
): string[] {
    let result = commandText;

    sortBy(currentSegments, (seg) => seg.start)
        .reverse()
        .filter((s) => s.segmentType !== 'UNIT')
        .forEach((segment) => {
            const before = result.slice(0, segment.start);
            const after = result.slice(segment.start + segment.length);
            result = before + after;
        });

    return result.split(WHITESPACE_REGEX).filter((str) => str.trim());
}

export function getUnitStaffCounts(
    currentSegments: Segment[],
    cliArgumentForArgumentDefinition: GenericObject<string>
): number[] {
    const staffCountArgumentDefinition = Object.entries(
        cliArgumentForArgumentDefinition
    ).find((argument) => argument.includes('STAFF_COUNT'))?.[0];

    const staffCountSegment =
        staffCountArgumentDefinition &&
        currentSegments.find(
            (segment) =>
                typeof segment.segmentData === 'string' &&
                segment.segmentData
                    .toLowerCase()
                    .includes(staffCountArgumentDefinition.toLowerCase())
        );

    if (!staffCountSegment) {
        return [];
    }

    const unitStaffCount = (staffCountSegment.segmentData as string).split(':')[1];
    return unitStaffCount ? [Number(unitStaffCount)] : [];
}

export function serialize(nodes: Descendant[]): string {
    return nodes.map((n) => Node.string(n)).join('\n');
}

export function findWordAtOffset(
    str: string,
    offset: number
): { left: number; right: number; word: string } {
    let left = offset;
    let right = offset;

    if (str[offset] === ' ' && str[offset - 1] !== ' ') {
        left -= 1;
    }

    while (left > 0 && str[left] !== ' ') {
        left -= 1;
    }

    while (str[right] && str[right] !== ' ') {
        right += 1;
    }

    return { left, right, word: str.slice(left, right) };
}

export function formatLocationForCli(
    location?: ConsolidatedLocationView,
    subpremisesEnabled?: boolean
): string {
    if (location == null) {
        return '';
    }
    const fullAddress = [
        location.placeName,
        location.streetAddress?.replace(' & ', '/'),
        location.rangeName ? `[${location.rangeName}]` : undefined,
        subpremisesEnabled ? getLocationSummarySubpremises(location) : undefined,
    ]
        .filter((s) => !!s)
        .join(', ');

    if (!location.areCoordinatesVerified) {
        return fullAddress;
    }

    return fullAddress;
}

export function locationStoreViewToCliAutocompleteOption(
    locationToken: string = '@',
    locationStoreView: LocationStoreView,
    subpremisesEnabled: boolean
): CliAutocompleteOption {
    return {
        cliArgument: 'LOCATION',
        segmentText: `${locationToken}${formatLocationForCli(
            locationStoreView,
            subpremisesEnabled
        )}${locationToken}`,
        title: formatLocationForCli(locationStoreView, subpremisesEnabled),
        value: locationStoreView.id,
    };
}

export function createFreeTextLocationStoreView(freeText: string): LocationStoreView {
    return {
        type: 'ADDRESS',
        entityLinks: [],
        streetAddress: freeText,
        isForcedResult: true,
        areCoordinatesVerified: false,
        classified: false,
        departmentId: -1,
        dispatchArea: '',
        dispatchAreaLabel: '',
        id: -1,
        multiAgencyClassificationDtos: [],
        spatialReferenceId: -1,
        uuid: v4(),
    };
}

export function createEmptyCommand(commandText = ''): CommandLineHistoryView {
    const id = +new Date();
    const command: CommandLineHistoryView = {
        id,
        commandText,
        status: 'ACTIVE',
        hidden: true,
        cliArgumentConfigurationId: 0,
        startTime: `${id}`,
        endTime: '',
    };

    return command;
}

export function isType<V extends Segment['segmentType']>(val: V) {
    return (obj: Segment): obj is Extract<Segment, { segmentType: V }> => obj.segmentType === val;
}
